import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Card, Input, Modal, List, Tag, Space, message, Popconfirm, Select, Tooltip, Upload, Dropdown } from 'antd';
import { PlusOutlined, DownloadOutlined, DeleteOutlined, ReloadOutlined, EditOutlined, ImportOutlined, FullscreenOutlined, FullscreenExitOutlined, ZoomInOutlined, ZoomOutOutlined, AimOutlined, ProfileOutlined, PushpinOutlined, PushpinFilled } from '@ant-design/icons';
import MindElixir from 'mind-elixir';
import { cn } from 'mind-elixir/i18n';
import 'mind-elixir/style.css';
import JSZip from 'jszip';
import { toPng, toSvg } from 'html-to-image';
import { apiFetch, onDataChanged } from '../utils/api.js';

// 预置模板（运营规划 / 选品思路 / 产品活动策划 / 工作计划）
const TEMPLATES = [
  {
    key: 'blank', label: '空白画布',
    nodeData: { topic: '中心主题', children: [] },
  },
  {
    key: 'plan', label: '运营规划',
    nodeData: {
      topic: '运营规划', children: [
        { topic: '目标', children: [{ topic: '月度营收目标' }, { topic: '利润目标' }, { topic: '毛利率目标' }] },
        { topic: '店铺运营', children: [{ topic: '店铺A' }, { topic: '店铺B' }] },
        { topic: '渠道', children: [{ topic: '电商平台' }, { topic: '微信客户' }, { topic: '平台代发' }] },
        { topic: '数据监控', children: [{ topic: '每日订单/营收' }, { topic: '库存预警' }, { topic: '每周复盘' }] },
      ],
    },
  },
  {
    key: 'select', label: '选品思路',
    nodeData: {
      topic: '选品思路', children: [
        { topic: '市场需求', children: [{ topic: '搜索热度' }, { topic: '季节因素' }, { topic: '复购率' }] },
        { topic: '利润测算', children: [{ topic: '成本核算' }, { topic: '售价区间' }, { topic: '毛利率' }] },
        { topic: '竞争分析', children: [{ topic: '同类产品' }, { topic: '价格带' }, { topic: '评价口碑' }] },
        { topic: '供应链', children: [{ topic: '供应商' }, { topic: '备货周期' }, { topic: '起订量' }] },
        { topic: '风险', children: [{ topic: '滞销风险' }, { topic: '库存资金占用' }] },
      ],
    },
  },
  {
    key: 'activity', label: '产品活动策划',
    nodeData: {
      topic: '产品活动策划', children: [
        { topic: '活动目标', children: [{ topic: '冲销量' }, { topic: '拉新' }, { topic: '清库存' }] },
        { topic: '选品', children: [{ topic: '主打款' }, { topic: '引流款' }, { topic: '利润款' }] },
        { topic: '促销方案', children: [{ topic: '满减' }, { topic: '优惠券' }, { topic: '赠品' }] },
        { topic: '推广渠道', children: [{ topic: '平台推广' }, { topic: '微信' }, { topic: '直播' }] },
        { topic: '执行排期', children: [{ topic: '预热期' }, { topic: '爆发期' }, { topic: '返场' }] },
        { topic: '复盘', children: [{ topic: '投产比' }, { topic: '销量/利润' }] },
      ],
    },
  },
  {
    key: 'work', label: '工作计划',
    nodeData: {
      topic: '工作计划', children: [
        { topic: '今日重点', children: [{ topic: '订单处理' }, { topic: '补货' }, { topic: '售后跟进' }] },
        { topic: '本周任务', children: [{ topic: '上新' }, { topic: '活动准备' }] },
        { topic: '数据看板', children: [{ topic: '销售日报' }, { topic: '库存盘点' }] },
        { topic: '团队协作', children: [{ topic: '仓库对接' }, { topic: '客服培训' }] },
      ],
    },
  },
];

// 主题配色（mind-elixir 6.0 格式：palette 分支色数组 + cssVar 文字/节点色）
// palette 只放一个颜色 → 所有分支统一为同一个颜色
const THEMES = [
  {
    name: 'default', label: '珊瑚橙', type: 'light',
    palette: ['var(--brand)'],
    cssVar: {
      '--root-color': '#660000', '--main-color': '#660000', '--color': '#660000',
      '--root-bgcolor': '#FFFFFF', '--main-bgcolor': '#FFFFFF',
      '--main-border': '1px solid rgba(var(--brand-rgb),0.25)',
    },
  },
  {
    name: 'active', label: '活跃橙', type: 'light',
    palette: ['#FA541C'],
    cssVar: {
      '--root-color': '#660000', '--main-color': '#660000', '--color': '#660000',
      '--root-bgcolor': '#FFF7E6', '--main-bgcolor': '#FFFFFF',
      '--main-border': '1px solid rgba(250,84,28,0.3)',
    },
  },
  {
    name: 'business', label: '商务蓝', type: 'light',
    palette: ['#1E66F5'],
    cssVar: {
      '--root-color': '#0F1E4D', '--main-color': '#1E3A8A', '--color': '#1E3A8A',
      '--root-bgcolor': '#1E40AF', '--main-bgcolor': '#FFFFFF',
      '--main-border': '1px solid rgba(30,102,245,0.25)',
    },
  },
  {
    name: 'black', label: '纯黑色', type: 'light',
    palette: ['#000000'],
    cssVar: {
      '--root-color': '#FFFFFF', '--main-color': '#000000', '--color': '#000000',
      '--root-bgcolor': '#000000', '--main-bgcolor': '#FFFFFF',
      '--main-border': '1px solid rgba(0,0,0,0.3)',
    },
  },
];

// 兼容模板/旧导图数据：6.0 init 要求 data.nodeData 存在；没有则包一层
const ensureNodeData = (d) => {
  if (d && d.nodeData) return d;
  // 旧数据可能是 {topic, children} 或 null → 包成完整结构
  return { nodeData: d && d.topic !== undefined ? d : (TEMPLATES[0].nodeData) };
};

const nodeToElixir = (node) => ({
  topic: node.topic || '未命名',
  children: (node.children || []).map(nodeToElixir),
  ...(node.id ? { id: node.id } : {}),
});

export default function MindMapPage() {
  const [maps, setMaps] = useState([]);
  const [cur, setCur] = useState(null);       // {id, title}
  const curRef = useRef(null);                 // 始终指向最新 cur，避免自动保存闭包串到旧导图
  const [createModal, setCreateModal] = useState(false);
  const [renameModal, setRenameModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);   // 要改名的导图 {id, title}，避免误改当前打开的导图
  const [newTitle, setNewTitle] = useState('');
  const [selTemplate, setSelTemplate] = useState('blank');
  const [theme, setTheme] = useState('default');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);   // 上次自动保存时间（Date）
  const elRef = useRef(null);
  const mindRef = useRef(null);
  const saveTimer = useRef(null);
  const canvasCardRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // 全屏切换（画布占满整屏，解决画布太小看不到底部）
  const toggleFullscreen = async () => {
    const el = canvasCardRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen();
        setTimeout(() => fitViewRef.current && fitViewRef.current(), 350);
      } else {
        await document.exitFullscreen();
      }
    } catch (e) {}
  };
  const fitViewRef = useRef(null);
  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  const loadMaps = useCallback(() => {
    apiFetch('/api/mindmaps').then(r => r.json()).then(d => setMaps(d.data || [])).catch(() => {});
  }, []);

  useEffect(() => { loadMaps(); }, [loadMaps]);
  useEffect(() => onDataChanged(() => { loadMaps(); }), [loadMaps]);
  useEffect(() => { curRef.current = cur; }, [cur]);

  // 组件卸载前：兜底保存当前导图（防刷新/关页丢失最后 1.5s 内的编辑）
  useEffect(() => () => {
    const mind = mindRef.current, c = curRef.current;
    if (!mind || !c) return;
    try {
      const data = mind.getData();
      apiFetch('/api/mindmaps/' + c.id, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data }),
      });
    } catch (e) {}
  }, []);

  const doSave = useCallback(async (silent = false) => {
    const mind = mindRef.current;
    const c = curRef.current; // 用 ref 读当前导图，避免闭包串到旧导图
    if (!mind || !c) return;
    if (!silent) setSaving(true);
    try {
      const data = mind.getData();
      const res = await apiFetch('/api/mindmaps/' + c.id, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data }),
      }).then(r => r.json());
      if (res.success) {
        setLastSaved(new Date());
        if (!silent) message.success('已保存');
      } else if (!silent) message.error(res.error || '保存失败');
      loadMaps();
    } catch (e) { if (!silent) message.error('保存失败'); }
    if (!silent) setSaving(false);
  }, [loadMaps]);

  const applyTheme = useCallback((t) => {
    const mind = mindRef.current;
    if (!mind) return;
    const th = THEMES.find(x => x.name === t) || THEMES[0];
    try { mind.changeTheme && mind.changeTheme(th, true); } catch (e) {}
  }, []);

  // 适应画布：按「宽度、高度两个方向取最小缩放」确保内容完整可见，再居中
  // （scaleFit 只按宽度缩放，纵向高的导图会放不下，故自实现 fitView）
  const fitView = useCallback(() => {
    const mind = mindRef.current;
    const canvas = elRef.current;
    if (!mind || !canvas) return;
    try {
      const svg = canvas.querySelector('svg');
      if (!svg) return;
      const bbox = svg.getBBox();
      const vw = canvas.clientWidth, vh = canvas.clientHeight;
      if (!bbox.width || !bbox.height || !vw || !vh) return;
      const s = Math.min(vw / bbox.width, vh / bbox.height);
      const scale = Math.min(s, 1); // 最多放到原始大小
      mind.scale(scale);
      mind.toCenter();
    } catch (e) {}
  }, []);
  // 同步到 ref（供 toggleFullscreen 在前面调用）
  useEffect(() => { fitViewRef.current = fitView; }, [fitView]);

  // 读取当前缩放（从画布 transform）
  const getScale = useCallback(() => {
    const canvas = elRef.current;
    if (!canvas) return 1;
    const el = [...canvas.querySelectorAll('*')].find(x => ((x.style && x.style.transform) || '').includes('scale'));
    if (el) { const m = el.style.transform.match(/scale\(([0-9.]+)\)/); if (m) return parseFloat(m[1]); }
    return 1;
  }, []);
  const zoomIn = useCallback(() => { const m = mindRef.current; if (m) { try { m.scale(Math.min(getScale() + 0.15, 5)); } catch (e) {} } }, [getScale]);
  const zoomOut = useCallback(() => { const m = mindRef.current; if (m) { try { m.scale(Math.max(getScale() - 0.15, 0.1)); } catch (e) {} } }, [getScale]);
  const zoom100 = useCallback(() => { const m = mindRef.current; if (m) { try { m.scale(1); m.toCenter(); } catch (e) {} } }, []);

  // 为选中的节点添加概要（mind-elixir 原生：createSummary 会按选中节点算范围并进入编辑文字）
  const addSummary = useCallback(() => {
    const m = mindRef.current;
    if (!m) { message.warning('请先打开一张导图'); return; }
    const nodes = m.currentNodes;
    if (!nodes || nodes.length === 0) {
      message.warning('请先选中一个或多个节点（按住 Ctrl 可多选同一分支的兄弟节点）');
      return;
    }
    try {
      m.createSummary();
    } catch (e) { message.warning((e && e.message) || '无法添加概要'); }
  }, []);

  // 自定义画布拖拽平移（直接改 transform，绕过 mind.move 的边界限制，实现无限平移）
  const panState = useRef(null);
  const getMapTransform = () => {
    const m = mindRef.current;
    if (!m || !m.map) return { x: 0, y: 0, s: 1 };
    const t = m.map.style.transform || '';
    const mm = t.match(/translate3d\(([-\d.]+)px,\s*([-\d.]+)px/);
    const sm = t.match(/scale\(([-\d.]+)\)/);
    return { x: mm ? parseFloat(mm[1]) : 0, y: mm ? parseFloat(mm[2]) : 0, s: sm ? parseFloat(sm[1]) : 1 };
  };
  const onCanvasMouseDown = (e) => {
    if (e.button !== 0) return;
    const t = e.target;
    if (t.closest && (t.closest('[contenteditable]') || t.closest('input') || t.closest('textarea') || t.closest('button'))) return;
    panState.current = { x: e.clientX, y: e.clientY };
  };
  const onCanvasMouseMove = (e) => {
    if (!panState.current) return;
    const dx = e.clientX - panState.current.x, dy = e.clientY - panState.current.y;
    panState.current = { x: e.clientX, y: e.clientY };
    const m = mindRef.current;
    if (m && m.map) {
      const cur = getMapTransform();
      m.map.style.transform = `translate3d(${cur.x + dx}px, ${cur.y + dy}px, 0) scale(${cur.s})`;
    }
  };
  const onCanvasMouseUp = () => { panState.current = null; };

  // 打开导图
  const openMap = async (m) => {
    try {
      // 切换前：清除未触发的防抖定时器，并立即保存当前导图（避免编辑未保存就切走导致丢失）
      if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
      if (mindRef.current && curRef.current && String(curRef.current.id) !== String(m.id)) {
        try { await doSave(true); } catch (e) {}
      }
      const r = await apiFetch('/api/mindmaps/' + m.id).then(r => r.json());
      setCur({ id: m.id, title: m.title });
      setLastSaved(null);
      setTimeout(() => {
        const el = elRef.current;
        if (!el) return;
        // 销毁旧 mind 实例，避免切换导图时累积内存泄漏
        if (mindRef.current) { try { mindRef.current.destroy && mindRef.current.destroy(); } catch (e) {} mindRef.current = null; }
        el.innerHTML = '';
        const data = ensureNodeData(r.data && r.data.data);
        // 兼容旧导图：旧数据可能存了旧格式 theme（palette 对象而非数组），移除避免 linkDiv 出错，统一由 applyTheme 应用新格式
        if (data && data.theme) delete data.theme;
        try {
          const mind = new MindElixir({
            el, contextMenu: { locale: cn }, editable: true, keypress: true,
            mouseSelectionButton: 2, // 2=右键框选 → 左键拖拽空白处 = 平移（无限拉动）
            scaleMax: 5, scaleMin: 0.1, // 放大到 500%，缩小到 10%
          });
          mindRef.current = mind;
          // 6.0：init(data) 为 async，返回 Promise（数据缺 nodeData 时返回 Error 值）
          Promise.resolve(mind.init(data)).then(() => {
            setTimeout(() => {
              try { mind.scale(1); mind.toCenter(); } catch (e) {}
              // 隐藏 mind-elixir 节点编辑输入框的英文占位提示 "Enter text or '/' for commands"
              try { const ib = el.querySelector('#input-box'); if (ib) ib.placeholder = ''; } catch (e) {}
            }, 200);
            applyTheme(theme);
            // 监听变更 → 自动保存（防抖 1.5s）
            mind.bus.addListener('operation', () => {
              if (saveTimer.current) clearTimeout(saveTimer.current);
              saveTimer.current = setTimeout(() => doSave(true), 1500);
            });
          }).catch(e => { message.error('导图加载失败: ' + (e && e.message ? e.message : e)); });
        } catch (e) { message.error('导图加载失败: ' + e.message); }
      }, 50);
    } catch (e) { message.error('加载失败'); }
  };

  const createMap = async () => {
    const tpl = TEMPLATES.find(t => t.key === selTemplate) || TEMPLATES[0];
    const data = ensureNodeData(JSON.parse(JSON.stringify(tpl.nodeData)));
    const title = newTitle.trim() || tpl.label;
    const res = await apiFetch('/api/mindmaps', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, data }),
    }).then(r => r.json());
    if (res.success) {
      message.success('导图已创建');
      setCreateModal(false); setNewTitle('');
      loadMaps();
      openMap(res.data);
    } else message.error(res.error || '创建失败');
  };

  const renameMap = async () => {
    if (!renameTarget) return;
    const res = await apiFetch('/api/mindmaps/' + renameTarget.id, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: newTitle.trim() }),
    }).then(r => r.json());
    if (res.success) {
      message.success('已改名');
      setRenameModal(false);
      // 若改的是当前打开的导图，同步标题
      setCur(c => (c && c.id === renameTarget.id ? { ...c, title: newTitle.trim() } : c));
      setRenameTarget(null);
      loadMaps();
    } else message.error(res.error || '改名失败');
  };

  // 解析 .posm（ProcessOn 思维导图：ZIP 包内含 .pos JSON）→ mind-elixir nodeData
  const parsePosm = async (file) => {
    const zip = await JSZip.loadAsync(file);
    const posName = Object.keys(zip.files).find(n => n.toLowerCase().endsWith('.pos'));
    if (!posName) throw new Error('压缩包内未找到 .pos 数据文件');
    const posText = await zip.files[posName].async('string');
    const posData = JSON.parse(posText);
    const el = posData && posData.diagram && posData.diagram.elements;
    if (!el || !Array.isArray(el.children)) throw new Error('不是有效的 ProcessOn 思维导图文件');
    const conv = (n) => ({ topic: n.title || '未命名', children: (n.children || []).map(conv) });
    return { topic: el.title || '导入导图', children: el.children.map(conv) };
  };

  // 导入本地 .posm → 解析 → 转 mind-elixir → 自动排版（mind-elixir 负责布局）→ 保存并打开
  const onImportPosm = async (file) => {
    const hide = message.loading('正在解析并排版…', 0);
    try {
      const nodeData = await parsePosm(file);
      const title = (nodeData.topic && nodeData.topic !== '导入导图') ? nodeData.topic : file.name.replace(/\.posm$/i, '');
      const res = await apiFetch('/api/mindmaps', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, data: { nodeData } }),
      }).then(r => r.json());
      hide();
      if (res.success) {
        message.success(`已导入「${title}」，自动排版完成`);
        loadMaps();
        openMap(res.data);
      } else message.error(res.error || '导入失败');
    } catch (e) { hide(); message.error('导入失败：' + e.message); }
    return false; // 阻止 antd 默认上传
  };

  const delMap = async (m) => {
    const res = await apiFetch('/api/mindmaps/' + m.id, { method: 'DELETE' }).then(r => r.json());
    if (res.success) {
      message.success('已删除');
      if (cur && cur.id === m.id) {
        setCur(null);
        if (mindRef.current) { try { mindRef.current.destroy && mindRef.current.destroy(); } catch (e) {} }
        if (elRef.current) elRef.current.innerHTML = '';
        mindRef.current = null;
      }
      loadMaps();
    } else message.error(res.error || '删除失败');
  };

  // 切换置顶状态（不触发内容编辑的 updated_at，后端 PUT 已做对应处理）
  const togglePin = async (m, e) => {
    e.stopPropagation();
    const res = await apiFetch('/api/mindmaps/' + m.id, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pinned: !m.pinned }),
    }).then(r => r.json());
    if (res.success) loadMaps();
  };

  // 导出图片（PNG/SVG）：mind-elixir 6.0 节点是 HTML div、连线才是 SVG，
  // 不能只序列化 svg（会丢节点 → 半截），必须用 html-to-image 渲染整个 map-canvas DOM。
  const exportImage = async (type) => {
    const mind = mindRef.current;
    if (!mind) { message.warning('请先打开一张导图'); return; }
    const map = mind.map, nodes = mind.nodes;
    if (!map || !nodes) { message.warning('画布未就绪'); return; }
    const hide = message.loading('正在导出' + (type === 'svg' ? ' SVG' : ' PNG') + '…', 0);
    try {
      // 内容完整尺寸 = me-nodes 的 max-content 尺寸（fit-content 的 map-canvas 会被 container 收缩，故取 nodes 尺寸）
      const w = Math.max(nodes.scrollWidth, nodes.offsetWidth, 100);
      const h = Math.max(nodes.scrollHeight, nodes.offsetHeight, 100);
      const opts = {
        backgroundColor: '#ffffff',
        pixelRatio: 2, // 2x 高清
        cacheBust: true,
        width: w,
        height: h,
        // 覆盖克隆节点的 transform（复位平移/缩放）与尺寸，让完整导图 1:1 落在正坐标区
        style: {
          transform: 'translate3d(0px, 0px, 0) scale(1)',
          width: w + 'px',
          height: h + 'px',
        },
      };
      const dataUrl = type === 'svg' ? await toSvg(map, opts) : await toPng(map, opts);
      const ext = type === 'svg' ? 'svg' : 'png';
      const a = document.createElement('a');
      a.href = dataUrl; a.download = (cur ? cur.title : '思维导图') + '.' + ext; a.click();
      hide();
      message.success((type === 'svg' ? 'SVG 矢量图' : 'PNG 图片') + '已导出');
    } catch (e) {
      hide();
      message.error('导出失败: ' + (e && e.message ? e.message : e));
    }
  };

  // 导出 Markdown 大纲（通用文本格式，可导入 XMind / 幕布 / Obsidian / Notion 等）
  const exportMarkdown = () => {
    const mind = mindRef.current;
    if (!mind) { message.warning('请先打开一张导图'); return; }
    try {
      const data = mind.getData();
      const nd = (data && data.nodeData) ? data.nodeData : data;
      const lines = [];
      const walk = (node, depth) => {
        lines.push('  '.repeat(depth) + '- ' + String(node.topic || ''));
        (node.children || []).forEach(c => walk(c, depth + 1));
      };
      if (nd && nd.topic !== undefined) walk(nd, 0); else lines.push('- 空白导图');
      const md = lines.join('\n');
      const blob = new Blob(['\ufeff' + md], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = (cur ? cur.title : '思维导图') + '.md'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      message.success('Markdown 已导出');
    } catch (e) { message.error('导出失败: ' + (e && e.message ? e.message : e)); }
  };

  return (
    <div className="page-container" style={{ display: 'flex', gap: 16, alignItems: 'stretch', height: 'calc(100vh - 140px)' }}>
      {/* 左侧：导图列表 */}
      <Card size="small" style={{ width: 240, flex: '0 0 240px', overflow: 'auto' }}
        title={<span style={{ fontSize: 14 }}>🧠 思维导图</span>}
        extra={
          <Space size={4}>
            <Upload accept=".posm" showUploadList={false} beforeUpload={onImportPosm}>
              <Button size="small" icon={<ImportOutlined />}>导入</Button>
            </Upload>
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => { setNewTitle(''); setSelTemplate('blank'); setCreateModal(true); }}>新建</Button>
          </Space>
        }>
        <List
          size="small" dataSource={maps} locale={{ emptyText: '暂无导图，点"新建"创建' }}
          renderItem={m => (
            <List.Item
              style={{
                cursor: 'pointer', padding: '6px 4px',
                background: cur && cur.id === m.id ? '#FAEEDA' : (m.pinned ? '#FFF7F3' : 'transparent'),
                borderRadius: 8,
                border: cur && cur.id === m.id ? '1px solid #EF9F27' : (m.pinned ? '1px solid #FFCBB6' : '1px solid var(--brand-border)'),
                marginBottom: 4,
              }}
              onClick={() => openMap(m)}
              actions={[
                <Tooltip title={m.pinned ? '取消置顶' : '置顶'} key="p">
                  {m.pinned
                    ? <PushpinFilled style={{ fontSize: 13, color: 'var(--brand)' }} onClick={(e) => togglePin(m, e)} />
                    : <PushpinOutlined style={{ fontSize: 12, color: '#bfbfbf' }} onClick={(e) => togglePin(m, e)} />}
                </Tooltip>,
                <Tooltip title="改名" key="r"><EditOutlined style={{ fontSize: 12 }} onClick={(e) => { e.stopPropagation(); setNewTitle(m.title); setRenameTarget(m); setRenameModal(true); }} /></Tooltip>,
                <Popconfirm key="d" title="删除这张导图？" onConfirm={() => delMap(m)}>
                  <span onClick={(e) => e.stopPropagation()}><DeleteOutlined style={{ fontSize: 12, color: '#cf1322' }} /></span>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={<span style={{ fontSize: 13, fontWeight: m.pinned ? 600 : 500 }}>
                  {m.pinned && <PushpinFilled style={{ fontSize: 11, color: 'var(--brand)', marginRight: 4 }} />}
                  {m.title}
                </span>}
                description={<span style={{ fontSize: 11, color: '#8c8c8c' }}>{m.created_at || m.updated_at || ''}</span>}
              />
            </List.Item>
          )}
        />
      </Card>

      {/* 右侧：画布（全屏时占满整屏，背景白） */}
      <Card size="small" ref={canvasCardRef} style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#fff' }}
        styles={isFullscreen ? { body: { height: 'calc(100vh - 60px)' } } : {}}
        title={
          <Space size={8}>
            <span style={{ fontSize: 14 }}>{cur ? cur.title : '请选择或新建一张导图'}</span>
            {cur && <Tag color="#FAEEDA" style={{ color: 'var(--brand-strong)' }}>双击改字 · Tab 加子节点 · 回车加同级 · Del 删除 · 拖动调整</Tag>}
          </Space>
        }
        extra={
          <Space wrap>
            {cur && (
              <>
                <Select size="small" value={theme} style={{ width: 100 }} onChange={(v) => { setTheme(v); applyTheme(v); doSave(true); }}
                  options={THEMES.map(t => ({ value: t.name, label: t.label }))} />
                <Button size="small" icon={<ZoomOutOutlined />} onClick={zoomOut} title="缩小" />
                <Button size="small" icon={<ZoomInOutlined />} onClick={zoomIn} title="放大" />
                <Button size="small" icon={<AimOutlined />} onClick={zoom100} title="100%">100%</Button>
                <Button size="small" onClick={fitView}>适应画布</Button>
                <Button size="small" icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} onClick={toggleFullscreen}>{isFullscreen ? '退出全屏' : '全屏'}</Button>
                <Button size="small" icon={<ProfileOutlined />} onClick={addSummary} title="为选中的节点添加概要">添加概要</Button>
                <Tag color={saving ? '#FAEEDA' : '#EAF5E4'} style={{ marginInlineEnd: 0, fontSize: 12, color: saving ? 'var(--brand-strong)' : '#389E0D' }}>
                  {saving ? '保存中…' : (lastSaved ? '已自动保存 ' + lastSaved.toTimeString().slice(0, 8) : '自动保存已开启')}
                </Tag>
                <Dropdown
                  menu={{
                    items: [
                      { key: 'png', label: '导出 PNG 图片', icon: <DownloadOutlined /> },
                      { key: 'svg', label: '导出 SVG 矢量图', icon: <DownloadOutlined /> },
                      { key: 'md', label: '导出 Markdown 大纲', icon: <DownloadOutlined /> },
                    ],
                    onClick: ({ key }) => {
                      if (key === 'png') exportImage('png');
                      else if (key === 'svg') exportImage('svg');
                      else if (key === 'md') exportMarkdown();
                    },
                  }}
                >
                  <Button size="small" type="primary" icon={<DownloadOutlined />}>导出</Button>
                </Dropdown>
              </>
            )}
            {!cur && <Button size="small" icon={<ReloadOutlined />} onClick={loadMaps}>刷新</Button>}
          </Space>
        }>
        <div ref={elRef} id="mindmap-canvas" style={{ flex: 1, minHeight: 400, background: '#fff', borderRadius: 8, cursor: 'grab' }}
          onMouseDownCapture={onCanvasMouseDown} onMouseMoveCapture={onCanvasMouseMove} onMouseUpCapture={onCanvasMouseUp} onMouseLeaveCapture={onCanvasMouseUp} />
      </Card>

      {/* 新建弹窗 */}
      <Modal title="新建思维导图" open={createModal} onOk={createMap} onCancel={() => setCreateModal(false)} okText="创建" width={460}>
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 8, fontSize: 13, color: '#8c8c8c' }}>导图标题（留空用模板名）</div>
          <Input placeholder="如：8月运营规划" value={newTitle} onChange={e => setNewTitle(e.target.value)} maxLength={30} />
          <div style={{ margin: '14px 0 8px', fontSize: 13, color: '#8c8c8c' }}>选择模板</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {TEMPLATES.map(t => (
              <div key={t.key} onClick={() => setSelTemplate(t.key)}
                style={{ border: selTemplate === t.key ? '2px solid var(--brand)' : '1px solid #E8E8E8', borderRadius: 10, padding: '12px 14px', cursor: 'pointer', background: selTemplate === t.key ? '#FFF7F3' : '#fff' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#26201C' }}>{t.label}</div>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>{t.nodeData.children ? t.nodeData.children.length + ' 个分支' : '空白'}</div>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* 改名弹窗 */}
      <Modal title="重命名导图" open={renameModal} onOk={renameMap} onCancel={() => { setRenameModal(false); setRenameTarget(null); }} okText="保存" width={380}>
        <Input style={{ marginTop: 12 }} value={newTitle} onChange={e => setNewTitle(e.target.value)} maxLength={30} />
      </Modal>
    </div>
  );
}
